import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import PageLayout from '../components/PageLayout';
import { useToast } from '../context/ToastContext';
import { formatDateTime } from '../utils/formatDate';
import TablePagination from '../components/TablePagination';
import TableSearch from '../components/TableSearch';

const sourceLabel = {
  ct_log: 'Certificate Transparency',
  passive_dns: 'Passive DNS',
  whois: 'WHOIS',
};

const ManagementDiscoveredAssets = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/discovered-assets', {
        params: {
          page: pagination.currentPage,
          limit: 50,
          status: statusFilter || undefined,
          search: debouncedSearch || undefined,
        },
      });
      if (res.data.success) {
        setAssets(res.data.data);
        setPagination((p) => ({ ...p, ...res.data.pagination }));
      }
    } catch {
      toastError('Failed to load discovered assets');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, statusFilter, debouncedSearch, toastError]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const handleClaim = async (id) => {
    try {
      const res = await api.patch(`/api/discovered-assets/${id}/claim`);
      if (res.data.success) {
        toastSuccess(res.data.message || 'Domain added — verify TXT record');
        fetchAssets();
      }
    } catch (err) {
      toastError(err.response?.data?.error || 'Claim failed');
    }
  };

  const handleDismiss = async (id) => {
    try {
      await api.patch(`/api/discovered-assets/${id}/dismiss`);
      toastSuccess('Dismissed');
      fetchAssets();
    } catch {
      toastError('Dismiss failed');
    }
  };

  const handleIgnore = async (id) => {
    if (!window.confirm('Permanently ignore this asset? It will never resurface.')) return;
    try {
      await api.patch(`/api/discovered-assets/${id}/ignore-permanently`);
      toastSuccess('Permanently ignored');
      fetchAssets();
    } catch {
      toastError('Action failed');
    }
  };

  return (
    <PageLayout title="Discovered Assets">
      <div className="row mb-4">
        <div className="col d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h2 className="h4 mb-1">
              <i className="las la-radar me-2 text-info"></i>
              Discovered Assets
            </h2>
            <p className="text-muted mb-0">Passive shadow IT candidates across all users</p>
          </div>
          <Link to="/management/dashboard" className="btn btn-outline-secondary btn-sm">
            <i className="las la-arrow-left me-1"></i> Dashboard
          </Link>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-3">
          <div className="mb-3">
            <TableSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search hostname or parent domain…"
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
          {['new', 'claimed', 'dismissed', 'ignored_permanently'].map((s) => (
            <button
              key={s}
              type="button"
              className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => { setStatusFilter(s); setPagination((p) => ({ ...p, currentPage: 1 })); }}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Hostname</th>
                <th>Parent domain</th>
                <th>Source</th>
                <th>Status</th>
                <th>First seen</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-4"><div className="spinner-border spinner-border-sm" /></td></tr>
              ) : assets.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-4 text-muted">No discovered assets</td></tr>
              ) : assets.map((a) => (
                <tr key={a.id}>
                  <td className="fw-semibold">{a.hostname}</td>
                  <td>{a.parent_domain}</td>
                  <td><span className="badge bg-light text-dark border">{sourceLabel[a.discovery_source] || a.discovery_source}</span></td>
                  <td>{a.status.replace('_', ' ')}</td>
                  <td className="text-muted small">{formatDateTime(a.first_seen_at)}</td>
                  <td className="text-end">
                    {a.status === 'new' && (
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-success" onClick={() => handleClaim(a.id)}>Claim</button>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => handleDismiss(a.id)}>Dismiss</button>
                        <button type="button" className="btn btn-outline-danger" onClick={() => handleIgnore(a.id)}>Ignore</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <TablePagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            showingFrom={pagination.totalCount === 0 ? 0 : (pagination.currentPage - 1) * 50 + 1}
            showingTo={Math.min(pagination.currentPage * 50, pagination.totalCount)}
            onPageChange={(next) => setPagination((p) => ({ ...p, currentPage: next }))}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default ManagementDiscoveredAssets;
